import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import {
  GlobalAttributesDocument,
  useCreateGlobalAttributeMutation,
} from "#graphql/supergraphHooks";
import {
  type CreateGlobalAttributeInput,
  type CreateGlobalAttributeMutation,
  type CreateGlobalAttributeMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseCreateGlobalAttributeReturn {
  createGlobalAttribute: (
    input: CreateGlobalAttributeInput,
  ) => Promise<CreateGlobalAttributeMutation["createGlobalAttribute"] | null>;
  loading: boolean;
  error: ReturnType<typeof useCreateGlobalAttributeMutation>[1]["error"];
}

/**
 * DV-251 — create a steward-curated global attribute (the inline "create a new
 * global" path from the space-authoring attribute picker). `kind` is forced to
 * `global_steward` server-side. Refetches the `GlobalAttributes` catalog (the
 * query that feeds `globalLib` / `globalIdByKey`) so a newly minted global
 * appears in mounted lists — required because every read is `no-cache`, so
 * there's no normalized cache to update.
 */
export const useCreateGlobalAttribute = (
  options?: MutationHookOptions<
    CreateGlobalAttributeMutation,
    CreateGlobalAttributeMutationVariables
  >,
): UseCreateGlobalAttributeReturn => {
  const [mutate, { loading, error }] = useCreateGlobalAttributeMutation({
    refetchQueries: [GlobalAttributesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const createGlobalAttribute = useCallback(
    async (input: CreateGlobalAttributeInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createGlobalAttribute ?? null;
    },
    [mutate],
  );

  return { createGlobalAttribute, loading, error };
};
